/**
 * /itarget — Pre-class diagnostic wizard (handbook §9 Page 5).
 *
 * State machine (local to this page, mirroring backend session.status):
 *   PICK_TOPIC → QUESTIONS_READY → ANSWERS_COLLECTED → GROUPED
 *
 * The "generate" + "group" calls go through the backend, which in turn
 * calls the school-itarget-* agents (mock-degraded when those agents
 * are not yet deployed by JZ — see services/school-backend/app/services/ai.py).
 */
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchItargetSession,
  useClassWithRoster,
  useClasses,
  useGenerateItarget,
  useItargetSessions,
  useGroupItarget,
  useSubmitItargetAnswers,
  type AnswerStatus,
  type ItargetGroupingEntry,
  type ItargetQuestion,
  type ItargetSession,
  type Student,
  authHeaders,
} from "../../api/school";
import { CustomPromptInput } from "../../components/CustomPromptInput";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";
import { safeSvg } from "../../lib/safeSvg";
import { openItargetSheet } from "./itargetSheet";

const DIFFICULTY_COLOR: Record<ItargetQuestion["difficulty"], string> = {
  foundation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  standard: "bg-sky-50 text-sky-700 border-sky-200",
  challenge: "bg-amber-50 text-amber-700 border-amber-200",
};

export function ITargetPage() {
  const { t } = useTranslation();
  const classes = useClasses();
  const [classId, setClassId] = useState("");
  const effective = classId || classes.data?.[0]?.id || "";
  const classDetail = useClassWithRoster(effective);

  const [session, setSession] = useState<ItargetSession | null>(null);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <h1 className="text-2xl font-semibold text-slate-800">{t("itarget.title")}</h1>
        {session && (
          <button
            type="button"
            onClick={() => setSession(null)}
            className="text-sm text-sky-600 hover:underline"
          >
            {t("itarget.history.back")}
          </button>
        )}
        {classes.data && classes.data.length === 0 && (
          <span className="text-xs text-amber-600">{t("common.noClassAssigned")}</span>
        )}
        <select
          value={effective}
          onChange={(e) => {
            setClassId(e.target.value);
            setSession(null);
          }}
          className="px-3 py-1.5 rounded border border-slate-200 text-sm"
        >
          {classes.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </header>

      {!session && classDetail.data?.teacher_id && (
        <StepGenerate
          classId={effective}
          teacherId={classDetail.data.teacher_id}
          onGenerated={setSession}
        />
      )}

      {!session && <SessionHistory classId={effective} onOpen={setSession} />}

      {session && session.status !== "grouped" && classDetail.data && (
        <StepAnswerGrid
          session={session}
          // Skip inactive students — they shouldn't appear in the
          // diagnostic grid since they're not currently attending.
          // Status flips back to "active" via the roster dropdown if
          // the teacher wants them back in scope.
          students={classDetail.data.students.filter(
            (s) => s.status !== "inactive",
          )}
          onAnswersSaved={(updated) => setSession(updated)}
        />
      )}

      {session && session.status === "grouped" && (
        <StepGroupings session={session} onReset={() => setSession(null)} />
      )}
    </div>
  );
}

// ── Step 1: pick topic, generate questions ─────────────────────────────────

function StepGenerate({
  classId,
  teacherId,
  onGenerated,
}: {
  classId: string;
  teacherId: string;
  onGenerated: (s: ItargetSession) => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [topic, setTopic] = useState("");
  const [date, setDate] = useState(today);
  const [customPrompt, setCustomPrompt] = useState("");
  const { employees: myEmployees } = useMyEmployees();
  const [pickedSubject, setPickedSubject] = useState<string>("");
  const effectiveSubject = pickedSubject || (myEmployees[0]?.slug ?? "");
  const generate = useGenerateItarget();

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-medium text-slate-800 mb-1">{t("itarget.step1Title")}</h2>
      <p className="text-sm text-slate-500 mb-4">
        {t("itarget.step1Description")}
      </p>
      <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-3">
        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-slate-600 mb-1">{t("itarget.topicLabel")}</span>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t("itarget.topicPlaceholder")}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("itarget.dateLabel")}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
      </div>
      {myEmployees.length > 1 && (
        <label className="mb-2 flex items-center gap-2 text-sm">
          <span className="text-xs text-slate-600">{t("exams.create.subjectLabel")}</span>
          <select
            value={effectiveSubject}
            onChange={(e) => setPickedSubject(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {myEmployees.map((emp: { slug: string; name: string }) => (
              <option key={emp.slug} value={emp.slug}>{emp.name}</option>
            ))}
          </select>
        </label>
      )}
      <div className="mb-3">
        <CustomPromptInput
          value={customPrompt}
          onChange={setCustomPrompt}
          placeholder={t("itarget.customPromptPlaceholder")}
          surface="itarget"
          context={{ topic, lesson_date: date, subject: effectiveSubject || undefined }}
        />
      </div>
      <div className="flex items-center gap-3 justify-end">
        {generate.error && (
          <span className="text-xs text-red-600">{(generate.error as Error).message}</span>
        )}
        <button
          type="button"
          onClick={async () => {
            const session = await generate.mutateAsync({
              class_id: classId,
              teacher_id: teacherId,
              lesson_topic: topic.trim(),
              subject: effectiveSubject || undefined,
              lesson_date: date,
              custom_prompt: customPrompt.trim() || null,
            });
            onGenerated(session);
          }}
          disabled={!topic.trim() || generate.isPending}
          className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          {generate.isPending ? t("itarget.generating") : t("itarget.generate")}
        </button>
      </div>
    </div>
  );
}

// ── Step 2: collect per-student per-question answers ──────────────────────

function StepAnswerGrid({
  session,
  students,
  onAnswersSaved,
}: {
  session: ItargetSession;
  students: Student[];
  onAnswersSaved: (s: ItargetSession) => void;
}) {
  const { t } = useTranslation();
  type Cell = AnswerStatus | "";
  type Grid = Record<string, Record<number, Cell>>;
  const [grid, setGrid] = useState<Grid>(() => {
    const base: Grid = Object.fromEntries(
      students.map((s) => [
        s.id,
        Object.fromEntries(session.questions.map((q) => [q.id, "" as Cell])),
      ]),
    );
    // 历史记录重开:回填已保存的作答标记。
    for (const a of session.answers ?? []) {
      const row = base[a.student_id];
      if (row && a.question_id in row) row[a.question_id] = a.answer_status;
    }
    return base;
  });
  const [err, setErr] = useState<string | null>(null);
  const submitAnswers = useSubmitItargetAnswers(session.id);
  const groupCall = useGroupItarget(session.id);
  // 拍照识别(2026-09-19 学校反馈):上传批改好的答题照片 → 视觉模型识别
  // 每生每题对错 → 预填网格,老师核对后照常「保存并分组」。
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrNote, setOcrNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function recognizePhoto(file: File) {
    setOcrBusy(true);
    setErr(null);
    setOcrNote(null);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await fetch(`/api/school/itarget/${session.id}/answers/from-image`, { method: "POST", headers: authHeaders(), body: fd });
      if (!r.ok) {
        const b = (await r.json().catch(() => ({}))) as { detail?: string };
        throw new Error(b.detail ?? `HTTP ${r.status}`);
      }
      const data = (await r.json()) as { candidates: { student_id: string; answers: Record<string, AnswerStatus>; uncertain?: boolean }[]; mock_mode?: boolean };
      let matched = 0;
      let uncertain = 0;
      setGrid((prev) => {
        const next = { ...prev };
        for (const c of data.candidates) {
          if (!next[c.student_id]) continue;
          matched += 1;
          if (c.uncertain) uncertain += 1;
          const row = { ...next[c.student_id] };
          for (const [qid, st] of Object.entries(c.answers)) {
            if (Number(qid) in row) row[Number(qid)] = st;
          }
          next[c.student_id] = row;
        }
        return next;
      });
      setOcrNote(t("itarget.ocr.done", { n: matched, u: uncertain }) + (data.mock_mode ? t("itarget.ocr.mock") : ""));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setOcrBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function cellStyle(value: Cell): string {
    if (value === "correct") return "bg-emerald-500 text-white border-emerald-500";
    if (value === "partial") return "bg-amber-400 text-white border-amber-400";
    if (value === "incorrect") return "bg-rose-500 text-white border-rose-500";
    return "bg-white text-slate-400 border-slate-200";
  }

  function cycleCell(studentId: string, qid: number) {
    setGrid((prev) => {
      const cur = prev[studentId]?.[qid] ?? "";
      const next: Cell =
        cur === "" ? "correct" : cur === "correct" ? "partial" : cur === "partial" ? "incorrect" : "";
      return { ...prev, [studentId]: { ...prev[studentId], [qid]: next } };
    });
  }

  async function saveAndGroup() {
    setErr(null);
    const answers = [];
    for (const s of students) {
      for (const q of session.questions) {
        const cell = grid[s.id]?.[q.id];
        if (cell) {
          answers.push({ student_id: s.id, question_id: q.id, answer_status: cell });
        }
      }
    }
    try {
      // Empty submissions are allowed — the grouper falls back to
      // historical English profile when a student has no diagnostic
      // answers, so a "no one answered yet" pass still produces a
      // useful 3-tier grouping for the teacher to discuss.
      if (answers.length > 0) {
        await submitAnswers.mutateAsync(answers);
      }
      const grouped = await groupCall.mutateAsync();
      onAnswersSaved(grouped);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const busy = submitAnswers.isPending || groupCall.isPending;

  return (
    <>
      <section className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-medium text-slate-800">
            {t("itarget.step2Title", { topic: session.lesson_topic })}
          </h2>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => openItargetSheet(session.questions, { topic: session.lesson_topic, date: session.lesson_date })}
              className="px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              data-testid="itarget-print"
            >
              {t("itarget.printSheet")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void recognizePhoto(f); }} />
            <button
              type="button"
              disabled={ocrBusy || busy}
              onClick={() => fileRef.current?.click()}
              className="px-2.5 py-1 rounded border border-violet-300 bg-white text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              data-testid="itarget-ocr"
            >
              {ocrBusy ? t("itarget.ocr.busy") : t("itarget.ocr.button")}
            </button>
            {ocrNote && <span className="text-emerald-700">{ocrNote}</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {session.questions.map((q) => (
            <div
              key={q.id}
              className={`rounded border p-3 text-sm ${DIFFICULTY_COLOR[q.difficulty]}`}
            >
              <div className="text-xs font-semibold mb-1 flex justify-between">
                <span>Q{q.id} · {t(`itarget.difficulty.${q.difficulty}`)}</span>
                <span>{t(`common.skills.${q.skill}`, { defaultValue: q.skill })}</span>
              </div>
              <div className="text-slate-700">{q.question}</div>
              {safeSvg(q.figure_svg) && (
                <div
                  className="mt-2 rounded bg-white p-1 [&>svg]:h-auto [&>svg]:max-w-full"
                  data-testid="itarget-figure"
                  dangerouslySetInnerHTML={{ __html: safeSvg(q.figure_svg) as string }}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-40">{t("itarget.tableHeadStudent")}</th>
              {session.questions.map((q) => (
                <th key={q.id} className="text-center px-3 py-2 font-medium">
                  Q{q.id}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-800">{s.name_zh}</td>
                {session.questions.map((q) => {
                  const cell = grid[s.id]?.[q.id] ?? "";
                  return (
                    <td key={q.id} className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => cycleCell(s.id, q.id)}
                        className={`w-10 h-7 rounded text-xs font-medium border transition ${cellStyle(cell)}`}
                      >
                        {cell === "correct" ? "✓" : cell === "partial" ? "△" : cell === "incorrect" ? "✗" : "—"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {t("itarget.cellHint")}
          </div>
          <div className="flex items-center gap-3">
            {err && <span className="text-xs text-red-600">{err}</span>}
            <button
              type="button"
              onClick={saveAndGroup}
              disabled={busy}
              className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
            >
              {busy ? t("itarget.savingAndGrouping") : t("itarget.saveAndGroup")}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ── Step 3: show AI groupings ─────────────────────────────────────────────

function StepGroupings({
  session,
  onReset,
}: {
  session: ItargetSession;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const g = session.groupings;
  const tiers: { key: "foundation" | "standard" | "challenge"; color: string }[] = [
    { key: "foundation", color: "border-emerald-200 bg-emerald-50" },
    { key: "standard",   color: "border-sky-200 bg-sky-50" },
    { key: "challenge",  color: "border-amber-200 bg-amber-50" },
  ];

  return (
    <>
      <section className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-lg font-medium text-slate-800">
            {t("itarget.step3Title")}
          </h2>
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-sky-600 hover:underline"
          >
            {t("itarget.restart")}
          </button>
        </div>
        {g.summary && (
          <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
            {g.summary}
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {tiers.map((tier) => {
          const list = (g[tier.key] as ItargetGroupingEntry[] | undefined) ?? [];
          return (
            <section
              key={tier.key}
              className={`rounded-lg border p-4 ${tier.color}`}
            >
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {t("itarget.tierCount", { label: t(`itarget.tier.${tier.key}`), count: list.length })}
              </h3>
              {list.length === 0 ? (
                <p className="text-xs text-slate-500 italic">{t("itarget.tierEmpty")}</p>
              ) : (
                <ul className="space-y-2">
                  {list.map((row) => (
                    <li
                      key={row.student_id}
                      className="rounded bg-white/60 border border-white p-2 text-xs"
                    >
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-medium text-slate-800">
                          {row.student_name}
                        </span>
                        <span className="font-mono text-slate-600">
                          {t("itarget.scoreOutOf100", { score: row.diagnostic_score })}
                        </span>
                      </div>
                      <div className="text-slate-600">{row.reason}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

// ── 历史诊断记录(Jarod 2026-08-18:向导一次性,生成新的旧的就丢了) ──

const STATUS_BADGE: Record<ItargetSession["status"], string> = {
  draft: "bg-slate-100 text-slate-600",
  questions_ready: "bg-sky-50 text-sky-700",
  answers_collected: "bg-amber-50 text-amber-700",
  grouped: "bg-emerald-50 text-emerald-700",
};

function SessionHistory({
  classId,
  onOpen,
}: {
  classId: string;
  onOpen: (s: ItargetSession) => void;
}) {
  const { t } = useTranslation();
  // 默认只看自己建的;「本班全部」看同班其他老师的(学校反馈 2026-09-19:
  // 个人的诊断记录不该默认全员可见)。
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const sessions = useItargetSessions(classId, scope === "mine");
  const [openingId, setOpeningId] = useState<string | null>(null);

  if (!sessions.data?.length && scope === "mine" && !sessions.isLoading) {
    // 自己没有记录时仍给切换入口,否则老师以为功能没了。
    return (
      <section className="mt-6 rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{t("itarget.history.title")}</span>
        <ScopeToggle scope={scope} onChange={setScope} />
      </section>
    );
  }
  if (!sessions.data?.length) return null;

  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-white">
      <div className="px-4 pt-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-slate-700">{t("itarget.history.title")}</h2>
        <ScopeToggle scope={scope} onChange={setScope} />
      </div>
      <ul className="mt-2 divide-y divide-slate-100">
        {sessions.data.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              disabled={openingId !== null}
              onClick={async () => {
                setOpeningId(s.id);
                try {
                  onOpen(await fetchItargetSession(s.id));
                } finally {
                  setOpeningId(null);
                }
              }}
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left text-sm hover:bg-slate-50 disabled:opacity-60"
            >
              <span className="font-medium text-slate-800">{s.lesson_topic}</span>
              <span className="text-xs text-slate-500">{s.lesson_date}</span>
              {scope === "all" && s.teacher_name && (
                <span className="text-xs text-slate-500">{t("itarget.history.by", { name: s.teacher_name })}</span>
              )}
              <span className="text-xs text-slate-400">
                {s.questions.length} {t("itarget.history.questionsUnit")}
              </span>
              <span
                className={`rounded px-1.5 py-0.5 text-xs ${STATUS_BADGE[s.status]}`}
              >
                {t(`itarget.history.status.${s.status}`)}
              </span>
              {openingId === s.id && <span className="text-xs text-slate-400">…</span>}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ScopeToggle({ scope, onChange }: { scope: "mine" | "all"; onChange: (s: "mine" | "all") => void }) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex rounded border border-slate-200 overflow-hidden text-xs" role="group" data-testid="itarget-history-scope">
      {(["mine", "all"] as const).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={scope === k}
          className={`px-2.5 py-1 ${scope === k ? "bg-sky-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
        >
          {t(`itarget.history.${k}`)}
        </button>
      ))}
    </div>
  );
}

